import TableSearchBar from "./TableSearchBar";
import { useTranslation } from "react-i18next";
import "../../GenericTable.css";
import { SearchQueryEvent } from "../../Models/searchQueryEvent";
import { JSX, ReactNode, useEffect, useState } from "react";
import { PageQuery } from "../../../../../utils/models/pageQuery";
import { BoxHeaderTypography } from "../../../Typographies/BoxHeaderTypography";
import { faFilter, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PrimaryButton } from "../../../Buttons/PrimaryButton";
import Dropdown from "../../../dropdown/Dropdown";
import { IdLookUps } from "../../../../../utils/models/idLookUp";
import { StrucCol } from "../../../StructureElements/StrucCol";
import { StrucRow } from "../../../StructureElements/StrucRow";
import { HStack } from "../../../../shared/components/VStack";
import { ReactComponent as FilterIcon } from "../../../../../resources/icons/filter-icon.svg";
import { Checkbox } from "@mui/material";
import { useLogoContext } from "../../../../screens/design-editor2/contexts/logo-options.context";
import { buildAdditionalLogoFilterRecipes } from "../../../../screens/logo-list/vmHooks/useFunctionalListLogosViewModel";
import { VendorSearchFilter } from "./VendorSearchFilter";
import { CustomerSearchFilter } from "./CustomerSearchFilter";
import { AutocompleteFilter } from "./AutocompleteFilter";
import { Role } from "../../../../../core/models/entities/Role";
import { useProfileContext } from "../../../../../contexts/ProfileContext";
import { Organization } from "../../../../../core/models/entities/Organization";

export interface ListHeaderProps {
  tableTitle: string | ReactNode;
  onSearchQueryChange?: (nextSearchQuery: SearchQueryEvent) => void;
  center: JSX.Element[] | JSX.Element;
  right: JSX.Element[] | JSX.Element;
  filters?: FilterRecipe<unknown>[];
  additionalFilters?: FilterRecipe<unknown>[];
  query?: PageQuery;
  onFilterChange?: (event: FilterChangeEvent) => void;
  showVendorFilter?: boolean;
  customers?: Organization[];
  storageKey?: string;
}

export interface FilterChangeEvent {
  activeFilters: FilterEvent[];
}

function ListHeader({
  tableTitle,
  onSearchQueryChange,
  center,
  right,
  query,
  filters,
  onFilterChange,
  showVendorFilter = false,
  customers,
  storageKey,
}: ListHeaderProps) {
  const { t } = useTranslation();
  // NOTE: Exceptionally, we need to show variations for name and number logo filter, so we use that in every header...
  const { options } = useLogoContext();
  const { role } = useProfileContext();
  const [openFilterBar, setOpenFilterBar] = useState<boolean>(
    role === Role.Admin,
  );
  let init: IdLookUps<FilterEvent["value"]> = {};
  if (query?.filterBy) {
    for (const filter of query?.filterBy) {
      init[filter.field] = filter.value;
    }
  }

  // HACK: Due to a lackluster abstraction layer, this abominable hack makes life easier.
  // See FunctionalList.tsx and its usages for explanation.
  const isLogoTable =
    typeof tableTitle === "string"
      ? tableTitle.toLowerCase() === "logos"
      : false;

  const [activeFilters, setActiveFilters] =
    useState<IdLookUps<FilterEvent["value"]>>(init);
  const [uncommittedFilter, setUncommittedFilter] =
    useState<IdLookUps<FilterEvent["value"]>>(activeFilters);
  const [nActiveFilters, setNActiveFilters] = useState<number>(0);
  const [nUncommittedFilters, setNUncommittedFilters] = useState<number>(0);
  const [openFilterId, setOpenFilterId] = useState<string | undefined>(
    undefined,
  );
  const [rememberFilters, setRememberFilters] = useState<boolean>(false);

  useEffect(() => {
    if (storageKey) {
      const stored = localStorage.getItem(storageKey + "_remember");
      if (stored === "true") {
        setRememberFilters(true);

        if (!query?.filterBy || query.filterBy.length === 0) {
          const storedFilters = localStorage.getItem(storageKey + "_filters");
          if (storedFilters) {
            try {
              const parsed = JSON.parse(storedFilters);
              // The "global logos only" checkbox is admin-only. A remembered
              // isForAllCustomers filter must not silently re-apply for other
              // roles (e.g. act-as/vendor) where the checkbox is hidden and
              // the filtered list would be unexplainable.
              if (role !== Role.Admin) {
                delete parsed["isForAllCustomers"];
              }
              setUncommittedFilter(parsed);
              setActiveFilters(parsed);
            } catch (e) {
              console.error("Failed to parse stored filters", e);
            }
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!onFilterChange) return;
    const result = [];
    for (const filterKey in activeFilters) {
      if (activeFilters[filterKey]) {
        result.push({
          key: filterKey,
          value: activeFilters[filterKey]!,
        });
      }
    }
    setNActiveFilters(result.length);
    onFilterChange({ activeFilters: result });
  }, [activeFilters]);

  useEffect(() => {
    if (!onFilterChange) {
      return;
    }
    const result = [];
    for (const filterKey in uncommittedFilter) {
      if (uncommittedFilter[filterKey]) {
        result.push({
          key: filterKey,
          value: uncommittedFilter[filterKey],
        });
      }
    }
    setNUncommittedFilters(result.length);
  }, [uncommittedFilter]);

  function handleFilterChange({ key, value }: FilterEvent) {
    setUncommittedFilter((af) => ({ ...af, [key]: value }));
  }

  function handleRemoveFilter(key: string) {
    setUncommittedFilter((af) => {
      const newFilters = { ...af };
      delete newFilters[key];
      return newFilters;
    });
  }

  function handleResetFilter() {
    setUncommittedFilter({});
  }

  function handleApplyFilters() {
    setActiveFilters(uncommittedFilter);
    // Only close filter bar for non-admin users
    if (role !== Role.Admin) {
      setOpenFilterBar(false);
    }

    if (storageKey && rememberFilters) {
      localStorage.setItem(
        storageKey + "_filters",
        JSON.stringify(uncommittedFilter),
      );
    }
  }

  function handleToggleFilters() {
    setOpenFilterId(undefined);

    const newState = !openFilterBar;
    if (!newState) {
      setUncommittedFilter(activeFilters);
    }
    setOpenFilterBar(newState);
  }

  const { logoType: logoTypeFilterSelection } = uncommittedFilter;
  const shouldUseAdditionalFilters = !!logoTypeFilterSelection;
  const isLogoType = (logoType: string) => logoType === logoTypeFilterSelection;
  const fontSizes = isLogoType("name")
    ? options.fontSizesNames
    : isLogoType("number")
      ? options.fontSizesNumbers
      : options.fontSizes;
  const colors = options.allColors;
  const fonts = options.fonts;
  const additionalFilters = shouldUseAdditionalFilters
    ? buildAdditionalLogoFilterRecipes(fontSizes, colors, fonts)
    : undefined;

  return (
    <StrucRow className="relative">
      <HStack className="w-full flex-wrap justify-end gap-x-7 gap-y-2">
        {typeof tableTitle === "string" ? (
          <BoxHeaderTypography>{tableTitle}</BoxHeaderTypography>
        ) : (
          tableTitle
        )}
        <div className="flex-1" />
        {filters && (
          <FilterMenuButton
            activeFilters={nActiveFilters}
            onFilterClick={handleToggleFilters}
          />
        )}
        {center}
        {onSearchQueryChange && (
          <div className="flex-[1.6] min-w-[300px]">
            <TableSearchBar
              searchQuery={query?.searchTerm}
              onSearchQueryChange={onSearchQueryChange}
              border={true}
              grayBG={true}
            />
          </div>
        )}
        {right}
      </HStack>
      {filters && openFilterBar && (
        <StrucRow
          style={{ width: "-webkit-fill-available" }}
          className="flex-1 fill mx-[-32px] bg-white pr-8 pl-8 pt-2 border-b space-y-2 z-10"
        >
          <button className="italic text-xs" onClick={handleResetFilter}>
            {t("compGenericTable.resetFilters")}
          </button>
          <StrucRow className="pb-4">
            <StrucCol size={role === Role.Admin ? 13 : 11}>
              <StrucRow className="space-x-2">
                {/* Vendor Search Filter - Only visible for Admins */}
                {showVendorFilter && role === Role.Admin && (
                  <VendorSearchFilter
                    open={openFilterId === "vendorFilter"}
                    filterKey="customer.id"
                    onOptionSelect={handleFilterChange}
                    onRemoveFilter={handleRemoveFilter}
                    initialValue={uncommittedFilter["customer.id"]}
                    defaultTitle={t("vendor")}
                    onOpenToggle={(open) =>
                      setOpenFilterId(open ? "vendorFilter" : undefined)
                    }
                  />
                )}
                {/* Customer Search Filter - Only visible for Vendor+ users */}
                {role && role >= Role.Vendor && customers && (
                  <CustomerSearchFilter
                    open={openFilterId === "customer.id"}
                    filterKey="customer.id"
                    onOptionSelect={handleFilterChange}
                    onRemoveFilter={handleRemoveFilter}
                    initialValue={uncommittedFilter["customer.id"]}
                    defaultTitle={t("customer")}
                    customers={customers}
                    onOpenToggle={(open) =>
                      setOpenFilterId(open ? "customer.id" : undefined)
                    }
                  />
                )}
                {filters
                  ?.filter((x) => x.key !== "customer.id")
                  .map((x) =>
                    x.autocomplete ? (
                      <AutocompleteFilter
                        key={x.key}
                        filterKey={x.key}
                        onOptionSelect={handleFilterChange}
                        initialValue={uncommittedFilter[x.key]}
                        options={x.options}
                        getOptionDisplay={x.getOptionDisplay}
                        defaultTitle={x.defaultTitle}
                        getOptionValue={x.getOptionValue}
                        narrowValue={
                          x.narrowByKey
                            ? uncommittedFilter[x.narrowByKey]
                            : undefined
                        }
                        narrowOption={x.narrowOption}
                        narrowHint={x.narrowHint}
                      />
                    ) : (
                      <Filter
                        open={openFilterId == x.key}
                        key={x.key}
                        filterKey={x.key}
                        onOptionSelect={handleFilterChange}
                        onRemoveFilter={handleRemoveFilter}
                        initialValue={uncommittedFilter[x.key]}
                        options={x.options}
                        getOptionDisplay={x.getOptionDisplay}
                        defaultTitle={x.defaultTitle}
                        getOptionValue={x.getOptionValue}
                        onOpenToggle={(open) =>
                          setOpenFilterId(open ? x.key : undefined)
                        }
                      />
                    ),
                  )}
              </StrucRow>
              {additionalFilters && (
                <StrucRow className="space-x-2 mt-2">
                  {additionalFilters?.map((x) => (
                    <Filter
                      open={openFilterId == x.key}
                      key={x.key}
                      filterKey={x.key}
                      onOptionSelect={handleFilterChange}
                      onRemoveFilter={handleRemoveFilter}
                      initialValue={uncommittedFilter[x.key]}
                      options={x.options}
                      getOptionDisplay={x.getOptionDisplay}
                      defaultTitle={x.defaultTitle}
                      getOptionValue={x.getOptionValue}
                      onOpenToggle={(open) =>
                        setOpenFilterId(open ? x.key : undefined)
                      }
                    />
                  ))}
                </StrucRow>
              )}
              {/* We want to show this for Logo table, even if title is customized. 
                  Ideally we pass a prop for this, but for now if options are present (Logo context) we might assume. 
                  Or better, check if we are not in design context? 
                  Let's rely on isLogoTable if string, OR if options exists and NOT design? 
                  Actually, simpler: if tableTitle is ReactNode (our Toggle), we assume it's the Logo screen for now.
              */}
              {(isLogoTable || typeof tableTitle !== "string") && (
                <StrucRow className="space-x-4">
                  <label>
                    {/* NOTE: Since inStorage is not part of the foldable filter UI, we should not try to commit it through `uncommittedFilters`.
                      This may cause confusion in a refactoring task where the inStorage filter should behave differently. */}
                    <Checkbox
                      checked={Boolean(uncommittedFilter["inStorage"])}
                      onChange={(event) => {
                        uncommittedFilter["inStorage"] = event.target.checked;
                        setUncommittedFilter({ ...uncommittedFilter });
                      }}
                    />
                    {t("compGenericTable.onlyShowLogosInStorage")}
                  </label>

                  {role === Role.Vendor && (
                    <label>
                      <Checkbox
                        checked={Boolean(uncommittedFilter["showOnlyOwned"])}
                        onChange={(event) => {
                          uncommittedFilter["showOnlyOwned"] =
                            event.target.checked;
                          setUncommittedFilter({ ...uncommittedFilter });
                        }}
                      />
                      {t("compGenericTable.onlyShowMyLogos")}
                    </label>
                  )}

                  {/* Global (stock) logos only - admin-only. Flows through the
                      normal filterBy plumbing as
                      filterByField=isForAllCustomers&filterByValue=true (the
                      unchecked/false value is dropped like inStorage's). */}
                  {role === Role.Admin && (
                    <label>
                      <Checkbox
                        checked={Boolean(
                          uncommittedFilter["isForAllCustomers"],
                        )}
                        onChange={(event) => {
                          // Build a NEW object - uncommittedFilter can share
                          // its reference with activeFilters, so an in-place
                          // mutation would leak into the committed filters
                          // without Apply. Drop the key entirely when
                          // unchecked (false is not a meaningful filter
                          // value).
                          const next = { ...uncommittedFilter };
                          if (event.target.checked) {
                            next["isForAllCustomers"] = true;
                          } else {
                            delete next["isForAllCustomers"];
                          }
                          setUncommittedFilter(next);
                        }}
                      />
                      {t("compGenericTable.globalLogosOnly")}
                    </label>
                  )}
                </StrucRow>
              )}

              {!isLogoTable &&
                typeof tableTitle === "string" &&
                role === Role.Vendor && (
                  <StrucRow className="space-x-4">
                    <label>
                      <Checkbox
                        checked={Boolean(uncommittedFilter["showOnlyOwned"])}
                        onChange={(event) => {
                          uncommittedFilter["showOnlyOwned"] =
                            event.target.checked;
                          setUncommittedFilter({ ...uncommittedFilter });
                        }}
                      />
                      {t("compGenericTable.onlyShowMyDesigns")}
                    </label>
                  </StrucRow>
                )}
            </StrucCol>
            <StrucCol size={1} className={role === Role.Admin ? "mt-2" : ""}>
              <StrucRow>
                <PrimaryButton
                  disabled={nUncommittedFilters === 0 && nActiveFilters === 0}
                  onClick={handleApplyFilters}
                >
                  {nUncommittedFilters === 0 && nActiveFilters === 0
                    ? t("compGenericTable.applyFilters")
                    : nUncommittedFilters === 0
                      ? t("compGenericTable.clearFilters")
                      : t("compGenericTable.applyFiltersCount", {
                          count: nUncommittedFilters,
                        })}
                </PrimaryButton>
              </StrucRow>
              {storageKey && (
                <StrucRow className="justify-center mt-1">
                  <label className="text-xs cursor-pointer flex items-center whitespace-nowrap">
                    <Checkbox
                      checked={rememberFilters}
                      size="small"
                      sx={{ padding: "4px" }}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setRememberFilters(checked);
                        localStorage.setItem(
                          storageKey + "_remember",
                          String(checked),
                        );
                        if (!checked) {
                          localStorage.removeItem(storageKey + "_filters");
                        }
                      }}
                    />
                    {t("compGenericTable.rememberFilters")}
                  </label>
                </StrucRow>
              )}
            </StrucCol>
          </StrucRow>
        </StrucRow>
      )}
    </StrucRow>
  );
}

interface FilterEvent {
  value: string | number | boolean | undefined;
  key: string;
}

export interface FilterRecipe<T> {
  key: string;
  options: T[];
  getOptionValue: (option: T) => string | number;
  getOptionDisplay?: (option: T) => string;
  initialValue?: string | number;
  defaultTitle: string;
  overwrite?: (option: T) => string | number;
  // Render as a searchable AutocompleteFilter instead of a plain Dropdown
  // (for large option sets). See AutocompleteFilter.tsx.
  autocomplete?: boolean;
  // Autocomplete only: narrow the options by another filter's (uncommitted)
  // value while that filter is set; searching all options otherwise.
  narrowByKey?: string;
  narrowOption?: (option: T, narrowValue: string | number | boolean) => boolean;
  // Autocomplete only: hint shown while the narrowing filter has no value.
  narrowHint?: string;
}

interface FilterProp<T> {
  open?: boolean;
  filterKey: string;
  options: T[];
  initialValue?: string | number | boolean;
  onOpenToggle: (open: boolean) => void;
  onOptionSelect: (event: FilterEvent) => void;
  onRemoveFilter?: (key: string) => void;
  getOptionValue: (option: T) => string | number;
  getOptionDisplay?: (option: T) => string;
  defaultTitle: string;
}

function Filter<T>({
  open,
  options,
  onOpenToggle,
  getOptionValue,
  onOptionSelect,
  onRemoveFilter,
  getOptionDisplay,
  initialValue,
  filterKey,
  defaultTitle,
}: FilterProp<T>) {
  const { t } = useTranslation();
  const option = options.find(
    (x) => "" + getOptionValue(x) === "" + initialValue,
  );

  function handleOptionSelect(data?: T) {
    onOptionSelect({
      value: data ? getOptionValue(data) : undefined,
      key: filterKey,
    });
  }

  const hasActiveFilter = initialValue !== undefined && initialValue !== "";

  return (
    <div className="relative">
      <Dropdown
        selectedOption={option}
        onOptionSelect={handleOptionSelect}
        options={options}
        getOptionDisplay={
          getOptionDisplay ?? ((datum: T) => "" + getOptionValue(datum))
        }
        defaultTitle={defaultTitle}
        width={"large"}
        onOpenToggle={onOpenToggle}
        open={open}
      />
      {hasActiveFilter && onRemoveFilter && (
        <button
          className="absolute -top-1 -right-1 w-3 h-3 bg-gray-500 rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors z-10"
          onClick={() => onRemoveFilter(filterKey)}
          title={t("compGenericTable.removeFilterTitle", {
            title: defaultTitle,
          })}
        >
          <FontAwesomeIcon icon={faTimes} className="text-xs" />
        </button>
      )}
    </div>
  );
}

export default ListHeader;

function FilterMenuButton({
  onFilterClick,
  activeFilters,
}: {
  onFilterClick: () => void;
  activeFilters: number;
}) {
  const { t } = useTranslation();
  return (
    <HStack
      className={
        "cursor-pointer gap-2 hover:bg-gray-100 px-2 self-stretch rounded-lg"
      }
      onClick={onFilterClick}
    >
      <label className="italic text-xxs cursor-pointer whitespace-nowrap">
        {t("compGenericTable.activeFiltersCount", { count: activeFilters })}
      </label>
      <div className="relative flex items-center">
        {activeFilters > 0 && (
          <div
            className={
              "h-[7px] w-[7px] rounded-full bg-success absolute top-[-2px] -right-1"
            }
          />
        )}
        <button>
          <FilterIcon />
        </button>
      </div>
    </HStack>
  );
}
